import { Component, Input, OnInit } from '@angular/core';
import { InfoItem } from '@frk/eds-components';
import { SummaryDetail } from '@models';
import { EMDASH } from '@products/utils/constants/product.constants';
import { TranslateService } from '@shared/translate/translate.service';
import { FundInformationPanel, OverviewChargesInfo } from '@types';

@Component({
  selector: 'ft-fund-information-panel',
  templateUrl: './fund-information-panel.component.html',
  styleUrls: ['./fund-information-panel.component.scss'],
})
export class FundInformationPanelComponent implements OnInit {
  @Input() panelData: FundInformationPanel;
  @Input() chargesInfo: OverviewChargesInfo;
  @Input() displayMifidCaveat: boolean;
  fundInfoHeader: string;
  asOfLabel: string;
  dataColumns: InfoItem[] = [];
  updateFrequency: string;

  constructor(private translateService: TranslateService) {}

  ngOnInit() {
    if (this.panelData.updateFrequency) {
      this.updateFrequency = `(${this.translateService.instant(
        this.panelData.updateFrequency
      )})`;
    }
    this.panelData.items.forEach((fundElement: SummaryDetail) => {
      this.dataColumns.push({
        label: fundElement.elementName,
        labelSmall: true,
        val: fundElement.elementValue,
        asOfLabel: this.translateService.instant('products.as-of'),
        // NGC-8404: remove 'asOfDate' from info item in case of 'TRADINGCHARACTERISTICS'.
        asOfDate:
          fundElement.sectionStd !== 'TRADINGCHARACTERISTICS'
            ? fundElement.asOfDate
            : null,
        caveatPlacement: `Fund Information:overview-${fundElement.elementNameStd}-fund-information`,
        secondaryCaveatPlacement: this.displayMifidCaveat
          ? `Fund Information:overview-${fundElement.elementNameStd}-fund-information-mifid`
          : null,
        tooltipName: fundElement?.elementName,
        tooltip: this.getTooltipContent(fundElement),
        // NGC-9106: add frequency in every section when 'preelemValue' is present. (Ref. PDS)
        updateFrequency: fundElement.preelemValue
          ? `(${this.translateService.instant(
              `products.updated-` + fundElement.preelemValue
            )})`
          : null,
      });
    });
  }

  /**
   * Get the tooltip content for the specific columns
   */
  getTooltipContent(fundElement): string {
    if (fundElement.elementNameStd === 'TA_ID_ADM') {
      return this.translateService.tooltip('products.ppss-fi-adm');
    } else if (fundElement.elementNameStd === 'SFDR_category') {
      // WDE-929 add SFDR tooltip
      return this.translateService.tooltip(
        `products.sfdr-tooltip__${fundElement.elementValueStd}`
      );
    } else if (
      this.chargesInfo &&
      fundElement.elementNameStd === 'EXPRATIOWITHWVR_WEB'
    ) {
      return this.getNetExpenseRatioTooltipContent();
    } else {
      return fundElement.preelemValueStd;
    }
  }

  /**
   * Format the tooltip content from charges and return as string.
   */
  getNetExpenseRatioTooltipContent(): string {
    if (this.chargesInfo.waiverType && this.chargesInfo.waiverType !== 'None') {
      const title = this.translateService.instant(
        'products.exp-ratio-tootltip-title'
      );
      const waiverTypeText = this.translateService.instant(
        'products.exp-ratio-tootltip-waiver-type'
      );
      const waiverEndDateText = this.translateService.instant(
        'products.exp-ratio-tootltip-waiver-end-date'
      );
      const waiverEndDate = this.chargesInfo.waiverEndDate
        ? this.chargesInfo.waiverEndDate
        : EMDASH;

      return `<b>${title}</b><br><br>${waiverTypeText}:<b> ${this.chargesInfo.waiverType}</b><br>${waiverEndDateText}:<b> ${waiverEndDate}</b>`;
    }
  }
}
